// The view logic behind CompanyLogin's two steps, kept pure so it can be
// tested without a DOM (the vendor app has no jsdom).
//
// Every time here is an absolute epoch-ms deadline, computed once from the
// API's relative seconds. The screen re-renders against `now`, so a countdown
// never drifts however late a tick lands.
import type { CodeLoginLockReason, CodeLoginStartResponse } from '@l2u/shared/codeLogin';
import { ApiError } from '@l2u/ui/lib/api.ts';
import { errorText } from '@l2u/ui/lib/errorText.ts';

export type LoginView =
  | { step: 'email' }
  | { step: 'sent' }
  | { step: 'code'; factor: 'totp' | 'sms'; phoneHint: string; cooldownUntil: number | null }
  | { step: 'locked'; until: number; reason: CodeLoginLockReason };

export function viewAfterStart(res: CodeLoginStartResponse, nowMs: number): LoginView {
  switch (res.method) {
    case 'link':
      return { step: 'sent' };
    case 'totp':
      return { step: 'code', factor: 'totp', phoneHint: '', cooldownUntil: null };
    case 'sms':
      return {
        step: 'code',
        factor: 'sms',
        phoneHint: res.phoneHint,
        cooldownUntil: nowMs + res.cooldownSeconds * 1000,
      };
    case 'locked':
      return { step: 'locked', until: nowMs + res.retryAfterSeconds * 1000, reason: res.reason };
  }
}

/** Locked panel past its time → email; otherwise unchanged. */
export function viewAfterTick(view: LoginView, nowMs: number): LoginView {
  // Returning the same object lets React bail out of the re-render.
  return view.step === 'locked' && view.until <= nowMs ? { step: 'email' } : view;
}

export type VerifyFailure =
  | { kind: 'message'; text: string }
  | { kind: 'locked'; until: number; reason: CodeLoginLockReason }
  | { kind: 'restart' };

// Matches lockRetry's fallback in apps/api/src/routes/auth.ts: a lock refusal
// always carries its time (up to 6 hours, E7), so this only keeps a malformed
// one from showing 0.
const DEFAULT_LOCK_SECONDS = 15 * 60;

/** Keys on ApiError.details.reason; anything else → its message. */
export function verifyFailure(err: unknown, nowMs: number): VerifyFailure {
  const details =
    err instanceof ApiError && typeof err.details === 'object' && err.details !== null
      ? (err.details as { reason?: unknown; attemptsLeft?: unknown; retryAfterSeconds?: unknown })
      : {};
  switch (details.reason) {
    case 'invalid_code': {
      const left = details.attemptsLeft;
      if (typeof left !== 'number') return { kind: 'message', text: 'Incorrect code' };
      return {
        kind: 'message',
        text: `Incorrect code — ${left} ${left === 1 ? 'attempt' : 'attempts'} left`,
      };
    }
    case 'code_expired':
      return { kind: 'message', text: 'That code has expired. Send a new one.' };
    case 'busy':
      return { kind: 'message', text: 'Too many sign-ins right now — try again in a moment.' };
    case 'locked': {
      const seconds =
        typeof details.retryAfterSeconds === 'number'
          ? details.retryAfterSeconds
          : DEFAULT_LOCK_SECONDS;
      // verify only ever locks on wrong codes; the send cap is start's refusal.
      return { kind: 'locked', until: nowMs + seconds * 1000, reason: 'wrong_codes' };
    }
    case 'restart':
      return { kind: 'restart' };
    default:
      return { kind: 'message', text: errorText(err) };
  }
}

export function lockedMessage(until: number, nowMs: number, reason: CodeLoginLockReason): string {
  // Never "0 minutes": the tick that reaches `until` swaps the panel for step 1.
  // From 60 minutes (the 6-hour cap, E7) it counts whole hours, rounded up.
  const minutes = Math.max(1, Math.ceil((until - nowMs) / 60_000));
  const hours = Math.ceil((until - nowMs) / 3_600_000);
  const wait =
    minutes < 60
      ? `Try again in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`
      : `Try again in ${hours} ${hours === 1 ? 'hour' : 'hours'}.`;
  return reason === 'send_cap'
    ? `Too many codes sent. ${wait}`
    : `Too many incorrect codes. ${wait}`;
}

/** Whole seconds, ≥ 0. */
export function cooldownRemaining(until: number | null, nowMs: number): number {
  return until === null ? 0 : Math.max(0, Math.ceil((until - nowMs) / 1000));
}

export function smsPrompt(phoneHint: string): string {
  return phoneHint ? `We texted a code to ${phoneHint}.` : 'We texted a code to your phone.';
}
